import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, API } from "@/App";
import { saleBlock } from "@/lib/saleStatus";
import { tokenStore } from "@/lib/token";
import { currentProductId } from "@/lib/productAliases";
import axios from "axios";
import { toast } from "sonner";
import { 
  CreditCard, 
  Shield, 
  Check,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Settings,
  Radio,
  Tag,
  X,
  DollarSign,
  Download,
  Gift,
  ArrowUpCircle,
  ExternalLink,
  MessageCircle
} from "lucide-react";
import Navbar from "@/components/Navbar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Default fallback bot config
const DEFAULT_BOT = { id: "gestion", name: "Produit", color: "#5865F2", colorClass: "text-blue-500", bgClass: "bg-blue-500", btnClass: "bg-blue-500 hover:bg-blue-600", plans: [] };

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const [selectedBotType, setSelectedBotType] = useState(currentProductId(searchParams.get('bot') || 'gestion'));
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get('plan') || '');
  const [botName, setBotName] = useState("");
  const [discordToken, setDiscordToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [publicPromos, setPublicPromos] = useState([]);
  const [autoRenew, setAutoRenew] = useState(false);
  const [shopBots, setShopBots] = useState([]);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [waiver, setWaiver] = useState(false);
  // Passage au pack (lien « Passer au pack » du tableau de bord ou de la page produit).
  const upgradeRequested = searchParams.get('upgrade') === '1';
  const [upgradeQuote, setUpgradeQuote] = useState(null);
  
  const { user } = useAuth();
  const navigate = useNavigate();

  // Fetch shop bots and public promos
  useEffect(() => {
    axios.get(`${API}/shop/bots`).then(res => {
      setShopBots(res.data || []);
      const bot = (res.data || []).find(b => b.id === selectedBotType);
      if (bot && bot.plans && bot.plans.length > 0 && !selectedPlan) {
        setSelectedPlan(bot.plans[0].id);
      }
    }).catch(() => {});
    axios.get(`${API}/promo-codes/public`).then(res => {
      setPublicPromos(res.data || []);
    }).catch(() => {});
  }, [selectedBotType, selectedPlan]);

  useEffect(() => {
    if (!upgradeRequested || !user) return;
    axios.get(`${API}/upgrades/${selectedBotType}/quote`, { headers: { Authorization: `Bearer ${tokenStore.get()}` } })
      .then(res => {
        setUpgradeQuote(res.data);
        if (res.data?.eligible) setSelectedPlan('lifetime');
      })
      .catch(() => setUpgradeQuote({ eligible: false, reason: "Passage au pack indisponible" }));
  }, [upgradeRequested, user, selectedBotType]);
  const upgradeActive = upgradeRequested && !!upgradeQuote?.eligible && selectedPlan === 'lifetime';

  const currentBot = shopBots.find(b => b.id === selectedBotType) || DEFAULT_BOT;
  // Bot Discord : l'acheteur en devient buyer. Sans compte Discord relié, il est
  // détecté avec le token (propriétaire de l'application Discord du bot).
  const needsDiscord = !currentBot.downloadable && !!user && !user.discord_linked;

  const connectDiscord = () => {
    const token = tokenStore.get();
    const origin = encodeURIComponent(window.location.origin);
    window.location.href = `${API}/auth/discord/link/start?origin=${origin}&token=${token}`;
  };
  const pricingPlans = (currentBot.plans || []).map(p => ({ ...p, price: p.price }));
  const currentPlan = pricingPlans.find(p => p.id === selectedPlan) || pricingPlans[0];

  const filteredPublicPromos = useMemo(() =>
    publicPromos.filter(p => p.applies_to === 'all' || p.applies_to === selectedBotType || p.applies_to === selectedPlan),
    [publicPromos, selectedBotType, selectedPlan]
  );

  // Calculate discounted price
  // 100 % off: free order, validated without Stripe. Otherwise Stripe needs at
  // least 0.50 € (same rule as the server).
  const basePrice = upgradeActive ? upgradeQuote.final_price : (currentPlan?.price || 0);
  const getDiscountedPrice = () => {
    if (!currentPlan || !promoApplied) return basePrice;
    const price = basePrice;
    const off = promoApplied.discount_type === 'percentage'
      ? price * Math.min(promoApplied.discount_value, 100) / 100
      : promoApplied.discount_value;
    const left = Math.max(0, Math.round((price - off) * 100) / 100);
    return left === 0 ? 0 : Math.max(0.50, left);
  };
  const finalPrice = promoApplied ? getDiscountedPrice() : basePrice;
  const isFree = (!!promoApplied || upgradeActive) && finalPrice === 0;
  const discount = promoApplied ? basePrice - finalPrice : 0;

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    await handleApplyPromoWithCode(promoCode);
  };

  const handleApplyPromoWithCode = async (code) => {
    setPromoLoading(true);
    setPromoError("");
    try {
      const token = tokenStore.get();
      const response = await axios.post(`${API}/promo-codes/validate`, {
        code: code,
        bot_type: selectedBotType,
        plan_id: selectedPlan
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setPromoApplied(response.data);
      setPromoCode(code);
      toast.success(`Code promo ${response.data.code} appliqué !`);
    } catch (error) {
      setPromoError(error.response?.data?.detail || "Code invalide");
      setPromoApplied(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoApplied(null);
    setPromoCode("");
    setPromoError("");
  };

  const validateForm = () => {
    if (currentBot.downloadable) return true;
    const newErrors = {};

    if (!botName.trim()) {
      newErrors.botName = "Le nom du bot est requis";
    }

    if (!discordToken.trim()) {
      newErrors.discordToken = "Le token Discord est requis";
    } else if (discordToken.length < 50) {
      newErrors.discordToken = "Token Discord invalide";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCheckout = async () => {
    if (!validateForm()) return;
    if (!acceptTerms) { toast.error("Acceptez les conditions générales de vente pour continuer"); return; }
    if (currentBot.downloadable && !waiver) { toast.error("Cochez la case sur l'accès immédiat pour continuer"); return; }
    
    setLoading(true);
    
    try {
      const token = tokenStore.get();
      const response = await axios.post(`${API}/checkout/create`, {
        plan_id: selectedPlan,
        bot_type: selectedBotType,
        discord_token: currentBot.downloadable ? "" : discordToken,
        bot_name: currentBot.downloadable ? "" : botName,
        origin_url: window.location.origin,
        promo_code: promoApplied ? promoApplied.code : null,
        auto_renew: autoRenew && !isFree && !upgradeActive,
        upgrade: upgradeActive,
        withdrawal_waiver: !!currentBot.downloadable && waiver
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création du paiement");
      setLoading(false);
    }
  };

  // Produit en maintenance / hors service : achat désactivé (le serveur refuse aussi).
  const blocked = saleBlock(currentBot);
  if (blocked) {
    return (
      <div className="min-h-screen bg-vanta-base">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-xl mx-auto px-6 lg:px-8 text-center" data-testid="sale-blocked">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-4">{currentBot.name} : {blocked.short.toLowerCase()}</h1>
            <p className={`p-4 mb-8 ${blocked.cls}`}>{blocked.text}</p>
            <Button variant="ghost" onClick={() => navigate(-1)} className="text-text-secondary hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Produit FiveM vendu sur Tebex : pas de paiement sur le site.
  if (currentBot.tebex_url) {
    return (
      <div className="min-h-screen bg-vanta-base">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-xl mx-auto px-6 lg:px-8 text-center" data-testid="tebex-checkout">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-4">{currentBot.name} s'achète sur Tebex</h1>
            <p className="text-text-secondary mb-8">
              Tebex est le partenaire officiel de FiveM pour vendre des scripts. À l'achat, tu connectes ton compte Cfx.re :
              le script est ajouté à ton compte et se télécharge sur portal.cfx.re, avec les mises à jour.
            </p>
            <a href={currentBot.tebex_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white font-bold px-8 py-4">
              <ExternalLink className="w-5 h-5 mr-2" />
              Acheter sur Tebex
            </a>
            <div className="mt-6">
              <Button variant="ghost" onClick={() => navigate(-1)} className="text-text-secondary hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vanta-base">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Back Button */}
          <Button 
            data-testid="back-btn"
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-8 text-text-secondary hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Public Promo Banner */}
            {filteredPublicPromos.length > 0 && !promoApplied && (
              <div className="lg:col-span-5">
                {filteredPublicPromos.map((promo) => (
                  <div key={promo.code} className="bg-blue-500/10 border border-blue-500/30 p-4 flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <Tag className="w-5 h-5 text-blue-400" />
                      <span className="text-blue-400 font-medium">
                        Promo en cours : <code className="font-mono font-bold bg-blue-500/20 px-2 py-0.5">{promo.code}</code>
                        {' '} — {promo.discount_type === 'percentage' ? `${promo.discount_value}% de réduction` : `${promo.discount_value}€ de réduction`}
                        {promo.applies_to !== 'all' && ` sur ${promo.applies_to === 'gestion' ? 'Bot Gestion' : promo.applies_to === 'radio' ? 'Bot Radio' : `plan ${promo.applies_to}`}`}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => { setPromoCode(promo.code); handleApplyPromoWithCode(promo.code); }}
                      className="bg-blue-500 hover:bg-blue-600 text-white rounded-none text-xs"
                    >
                      Appliquer
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Form Section */}
            <div className="lg:col-span-3">
              <div className="bg-vanta-surface border border-border-default p-8">
                <h1 className="font-heading text-2xl font-bold tracking-tighter mb-2">
                  {currentBot.downloadable ? "Confirmer votre achat" : "Activer votre Bot"}
                </h1>
                <p className="text-text-secondary mb-8">
                  {currentBot.downloadable ? "Votre clé de licence et le lien de téléchargement apparaîtront dans votre tableau de bord après le paiement." : "Remplissez les informations de votre bot Discord"}
                </p>

                <div className="space-y-6">
                  {upgradeRequested && upgradeQuote && (
                    upgradeQuote.eligible ? (
                      <div data-testid="checkout-upgrade" className="bg-blue-500/10 border border-blue-500/30 p-4 flex items-start gap-3">
                        <ArrowUpCircle className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-white">Passage au pack</p>
                          <p className="text-xs text-text-muted">
                            {upgradeQuote.components.map(c => c.name).join(", ")} : {upgradeQuote.credit.toFixed(2)}€ déduits. Ces licences sont remplacées par celle du pack ; la clé déjà utilisée par votre serveur devient celle du pack.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-vanta-base border border-border-default p-4 text-sm text-text-muted">{upgradeQuote.reason || "Passage au pack indisponible"} : achat au prix normal.</div>
                    )
                  )}

                  {/* Bot Type Selection */}
                  <div>
                    <Label htmlFor="botType" className="text-white mb-2 block">
                      Produit
                    </Label>
                    <Select value={selectedBotType} onValueChange={setSelectedBotType} disabled={upgradeActive}>
                      <SelectTrigger 
                        data-testid="bot-type-select"
                        className="bg-vanta-base border-border-default focus:border-blue-500 rounded-none h-12 relative"
                      >
                        <SelectValue className="pr-8" />
                      </SelectTrigger>
                      <SelectContent className="bg-vanta-surface border-border-default">
                        {shopBots.map((bot) => (
                          <SelectItem 
                            key={bot.id} 
                            value={bot.id}
                            className="focus:bg-vanta-base"
                          >
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: bot.color }}></span>
                              <span>{bot.name}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Plan Selection */}
                  <div>
                    <Label htmlFor="plan" className="text-white mb-2 block">
                      Plan d'abonnement
                    </Label>
                    <Select value={selectedPlan} onValueChange={setSelectedPlan} disabled={upgradeActive}>
                      <SelectTrigger 
                        data-testid="plan-select"
                        className="bg-vanta-base border-border-default focus:border-blue-500 rounded-none h-12 relative"
                      >
                        <SelectValue className="pr-8" />
                      </SelectTrigger>
                      <SelectContent className="bg-vanta-surface border-border-default">
                        {pricingPlans.map((plan) => (
                          <SelectItem 
                            key={plan.id} 
                            value={plan.id}
                            className="focus:bg-vanta-base"
                          >
                            <span className="flex items-center gap-2">
                              <span>{plan.name}</span>
                              <span className="text-text-muted">- {plan.price?.toFixed?.(2) || plan.price}€ / {plan.duration_label || plan.duration}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {currentBot.downloadable ? (
                    <div className="bg-vanta-base border border-border-default p-4 flex items-start gap-3">
                      <Download className="w-5 h-5 text-status-success mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-white">Produit téléchargeable</p>
                        <p className="text-xs text-text-muted">
                          Aucune configuration Discord n'est nécessaire. Après le paiement, retrouvez le bouton de téléchargement et votre clé de licence (valable sur un seul appareil) dans votre tableau de bord.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Compte Discord de l'acheteur */}
                      <div
                        data-testid="checkout-discord"
                        className={`border p-4 flex items-start gap-3 ${needsDiscord ? 'border-[#5865F2]/40 bg-[#5865F2]/10' : 'border-green-500/30 bg-green-500/10'}`}
                      >
                        <MessageCircle className="w-5 h-5 text-[#8b95ff] mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-white">
                            {needsDiscord ? "Acheteur détecté automatiquement" : "Compte Discord connecté"}
                          </p>
                          <p className="text-xs text-text-muted mt-1">
                            {needsDiscord
                              ? "Tu seras buyer de ton bot : on détecte le compte Discord qui possède l'application du bot (celui qui l'a créé sur le Developer Portal). Pour utiliser un autre compte, connecte-le avant de payer."
                              : "Tu seras buyer et owner de ton bot sur Discord dès qu'il démarre (avec le propriétaire de l'application du bot)."}
                          </p>
                          {needsDiscord && (
                            <Button type="button" onClick={connectDiscord} variant="outline" className="mt-3 border-[#5865F2]/50 text-white hover:bg-[#5865F2]/20 rounded-none h-10 text-sm">
                              Connecter mon Discord
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Bot Name */}
                      <div>
                        <Label htmlFor="botName" className="text-white mb-2 block">
                          Nom du Bot
                        </Label>
                        <Input
                          id="botName"
                          data-testid="bot-name-input"
                          value={botName}
                          onChange={(e) => setBotName(e.target.value)}
                          placeholder="Mon Super Bot"
                          className={`bg-vanta-base border-border-default focus:border-blue-500 rounded-none h-12 ${errors.botName ? 'border-status-error' : ''}`}
                        />
                        {errors.botName && (
                          <p className="text-status-error text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.botName}
                          </p>
                        )}
                      </div>

                      {/* Discord Token */}
                      <div>
                        <Label htmlFor="discordToken" className="text-white mb-2 block">
                          Token Discord
                        </Label>
                        <Input
                          id="discordToken"
                          data-testid="discord-token-input"
                          type="password"
                          value={discordToken}
                          onChange={(e) => setDiscordToken(e.target.value)}
                          placeholder="MTIzNDU2Nzg5MDEyMzQ1Njc4OQ..."
                          className={`bg-vanta-base border-border-default focus:border-blue-500 rounded-none h-12 font-mono ${errors.discordToken ? 'border-status-error' : ''}`}
                        />
                        {errors.discordToken && (
                          <p className="text-status-error text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.discordToken}
                          </p>
                        )}
                        <p className="text-text-muted text-sm mt-2">
                          Votre token est crypté et sécurisé. Ne le partagez jamais.
                        </p>
                      </div>
                    </>
                  )}

                  {/* Auto-Renewal Toggle */}
                  {selectedPlan !== 'lifetime' && !isFree && !upgradeActive && (
                    <div className="bg-vanta-base border border-border-default p-4 rounded-none">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-white">Renouvellement automatique</p>
                          <p className="text-xs text-text-muted mt-1">
                            {autoRenew 
                              ? `${(finalPrice / Math.max(1, Math.floor((pricingPlans.find(p => p.id === selectedPlan)?.duration || '1 mois').split(' ')[0]) || 1)).toFixed(2)}€/mois — prélevé chaque mois automatiquement`
                              : 'Paiement unique — vous devrez renouveler manuellement'}
                          </p>
                        </div>
                        <button
                          data-testid="auto-renew-toggle"
                          onClick={() => setAutoRenew(!autoRenew)}
                          className={`w-12 h-6 rounded-full transition-colors relative ${autoRenew ? 'bg-blue-500' : 'bg-white/10'}`}
                        >
                          <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${autoRenew ? 'translate-x-6' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                      {autoRenew && (
                        <div className="mt-3 p-2 bg-blue-500/10 border border-blue-500/20 text-xs text-blue-400">
                          Si le paiement échoue (carte bloquée, etc.), votre produit sera automatiquement désactivé.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Promo Code */}
                  <div>
                    <Label htmlFor="promo" className="text-white mb-2 block text-sm font-medium">
                      Code promo (optionnel)
                    </Label>
                    {promoApplied ? (
                      <div className="flex items-center gap-2 p-3 bg-status-success/10 border border-status-success/30">
                        <Tag className="w-4 h-4 text-status-success" />
                        <span className="text-status-success font-mono font-bold">{promoApplied.code}</span>
                        <span className="text-status-success text-sm">
                          -{promoApplied.discount_type === 'percentage' ? `${Math.min(promoApplied.discount_value, 100)}%` : `${promoApplied.discount_value}€`}
                        </span>
                        <button onClick={handleRemovePromo} className="ml-auto text-text-muted hover:text-white">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div className="flex gap-2">
                          <Input
                            id="promo"
                            data-testid="promo-code-input"
                            value={promoCode}
                            onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(""); }}
                            placeholder="PROMO20"
                            className="bg-vanta-base border-border-default rounded-none h-12 font-mono uppercase flex-1"
                          />
                          <Button
                            data-testid="apply-promo-btn"
                            onClick={handleApplyPromo}
                            disabled={promoLoading || !promoCode.trim()}
                            variant="outline"
                            className="border-border-default rounded-none h-12 px-6"
                          >
                            {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Appliquer"}
                          </Button>
                        </div>
                        {promoError && (
                          <p className="text-status-error text-sm mt-1">{promoError}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* CGV + accès immédiat (renonciation au droit de rétractation) */}
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 text-sm text-text-secondary cursor-pointer">
                      <input data-testid="accept-terms" type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} className="mt-1 accent-blue-500" />
                      <span>J'ai lu et j'accepte les <Link to="/cgv" target="_blank" className="text-blue-400 underline">conditions générales de vente</Link>.</span>
                    </label>
                    {currentBot.downloadable && (
                      <label className="flex items-start gap-3 text-sm text-text-secondary cursor-pointer">
                        <input data-testid="withdrawal-waiver" type="checkbox" checked={waiver} onChange={(e) => setWaiver(e.target.checked)} className="mt-1 accent-blue-500" />
                        <span>Je demande l'accès immédiat au produit après le paiement et je reconnais perdre mon droit de rétractation (article L221-28 13° du Code de la consommation).</span>
                      </label>
                    )}
                  </div>

                  {/* Security Notice */}
                  <div className="bg-vanta-base border border-border-default p-4 flex items-start gap-3">
                    <Shield className="w-5 h-5 text-status-success mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white">Paiement sécurisé</p>
                      <p className="text-xs text-text-muted">
                        Vos informations sont protégées par cryptage SSL. Paiement géré par Stripe.
                      </p>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    data-testid="checkout-btn"
                    onClick={handleCheckout}
                    disabled={loading || !acceptTerms || (currentBot.downloadable && !waiver)}
                    className="w-full py-6 rounded-none font-bold text-lg btn-hover-lift text-white"
                    style={{ backgroundColor: currentBot.color || '#5865F2' }}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Redirection...
                      </>
                    ) : (
                      <>
                        {isFree ? <Gift className="w-5 h-5 mr-2" /> : <CreditCard className="w-5 h-5 mr-2" />}
                        {isFree ? "Obtenir gratuitement" : `Payer ${finalPrice.toFixed(2)}€`}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-2">
              <div className="bg-vanta-surface border border-border-default p-6 sticky top-24">
                <h2 className="font-heading text-lg font-semibold mb-6">Récapitulatif</h2>
                
                <div className="space-y-4 mb-6">
                  {/* Bot Info */}
                  <div className="flex items-center gap-4 p-4 bg-vanta-base border border-border-default">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${currentBot.color || '#5865F2'}20` }}>
                      <CreditCard className="w-6 h-6" style={{ color: currentBot.color || '#5865F2' }} />
                    </div>
                    <div>
                      <p className="font-semibold">{currentBot.name}</p>
                      <p className="text-sm" style={{ color: currentBot.color || '#5865F2' }}>{(currentBot.plans || []).length} plans</p>
                    </div>
                  </div>
                  
                  {/* Plan Info */}
                  <div className="flex items-center gap-4 p-4 bg-vanta-base border border-border-default">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${currentBot.color || '#5865F2'}20` }}>
                      <DollarSign className="w-6 h-6" style={{ color: currentBot.color || '#5865F2' }} />
                    </div>
                    <div>
                      <p className="font-semibold">{currentPlan?.name}</p>
                      <p className="text-sm text-text-muted">{currentPlan?.duration_label || currentPlan?.duration}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-border-default pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Sous-total</span>
                    <span>{(currentPlan?.price || 0).toFixed(2)}€</span>
                  </div>
                  {upgradeActive && (
                    <div className="flex justify-between text-sm">
                      <span className="text-blue-400">Déjà possédé</span>
                      <span className="text-blue-400">-{upgradeQuote.credit.toFixed(2)}€</span>
                    </div>
                  )}
                  {promoApplied && (
                    <div className="flex justify-between text-sm">
                      <span className="text-status-success">Promo {promoApplied.code}</span>
                      <span className="text-status-success">-{discount.toFixed(2)}€</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Mode</span>
                    <span className={autoRenew ? 'text-blue-400' : 'text-text-secondary'}>{autoRenew ? 'Abonnement mensuel' : 'Paiement unique'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">TVA</span>
                    <span>Incluse</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg pt-2 border-t border-border-default">
                    <span>Total</span>
                    <span style={{ color: currentBot.color || '#5865F2' }}>{finalPrice.toFixed(2)}€{autoRenew && selectedPlan !== 'lifetime' ? '/mois' : ''}</span>
                  </div>
                </div>

                <div className="mt-6 space-y-2">
                  {[
                    "Activation instantanée",
                    "Support 24/7",
                    "Mises à jour incluses",
                    "Sécurité garantie"
                  ].map((feature) => (
                    <div key={feature} className="flex items-center gap-2 text-sm text-text-secondary">
                      <Check className="w-4 h-4" style={{ color: currentBot.color || '#5865F2' }} />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
